import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { api, asArray, errMsg, formatDate } from "../../lib/api";
import { Empty, Pill, readFileAsDataUrl } from "./shared";

const CATS = ["SEBI Circulars", "Stock Broker", "Depository Participant", "Research Analyst", "Investment Adviser", "PMS", "AIF", "PMLA", "Audit & Inspection"];
const EMPTY = { title: "", excerpt: "", content: "", category: "SEBI Circulars", cover_image: "", published: true };

const BlogForm = ({ initial, onSaved }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const onFile = async (e) => {
    try { setF({ ...f, cover_image: await readFileAsDataUrl(e.target.files?.[0], 800) }); }
    catch (err) { toast.error(err.message); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { title: f.title, excerpt: f.excerpt, content: f.content, category: f.category, cover_image: f.cover_image, published: f.published };
      if (f.id) await api.put(`/admin/blogs/${f.id}`, payload);
      else await api.post("/admin/blogs", payload);
      toast.success(f.id ? "Article updated" : "Article published");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form data-testid="blog-form" onSubmit={save} className="space-y-4">
      <div><Label>Title</Label><Input data-testid="blog-title-input" required minLength={3} value={f.title} onChange={set("title")} /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Category</Label>
          <select data-testid="blog-category-select" value={f.category} onChange={set("category")} className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><Label>Cover image (upload or URL)</Label>
          <div className="flex gap-2">
            <Input data-testid="blog-cover-url-input" placeholder="https://… or /images/…" value={f.cover_image.startsWith("data:") ? "(uploaded image)" : f.cover_image} onChange={set("cover_image")} />
            <label className="shrink-0 cursor-pointer rounded-md border border-input px-3 text-sm grid place-items-center hover:bg-paper-2">Upload<input data-testid="blog-cover-file-input" type="file" accept="image/*" className="hidden" onChange={onFile} /></label>
          </div>
        </div>
      </div>
      <div><Label>Short summary</Label><Textarea data-testid="blog-excerpt-input" rows={2} value={f.excerpt} onChange={set("excerpt")} /></div>
      <div>
        <Label>Content</Label>
        <Textarea data-testid="blog-content-input" required minLength={10} rows={12} value={f.content} onChange={set("content")} className="font-mono text-sm" />
        <p className="mt-1 text-xs text-ink/60">Tip: start a line with "## " for a heading, "- " for a bullet point. Leave a blank line between paragraphs.</p>
      </div>
      <label className="flex items-center gap-3"><Switch data-testid="blog-published-switch" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} /> <span className="text-sm">Published</span></label>
      <Button data-testid="blog-save-button" disabled={busy} className="w-full">{busy ? "Saving…" : "Save article"}</Button>
    </form>
  );
};

export const BlogsTab = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blogs").then((r) => setItems(asArray(r))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try { await api.delete(`/admin/blogs/${b.id}`); toast.success("Deleted"); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display font-bold text-2xl">Insights / blogs</h2>
        <Button data-testid="blog-new-button" onClick={() => setEditing(EMPTY)}><Plus className="w-4 h-4 mr-1" /> New article</Button>
      </div>
      {!items.length && <Empty testid="admin-blogs-empty">No articles yet.</Empty>}
      <div className="space-y-3">
        {items.map((b) => (
          <div key={b.id} data-testid={`admin-blog-${b.id}`} className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl bg-white border border-ink/10 p-4">
            {b.cover_image ? <img src={b.cover_image} alt="" className="w-full sm:w-24 h-32 sm:h-16 object-cover rounded-lg" /> : <div className="hidden sm:block w-24 h-16 rounded-lg bg-paper-2" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{b.title}</p>
              <p className="text-xs text-ink/60 mt-1 flex flex-wrap gap-2 items-center">{b.category} · {formatDate(b.created_at)} {b.published ? <Pill tone="green">Published</Pill> : <Pill tone="amber">Draft</Pill>}</p>
            </div>
            <div className="flex gap-2">
              {b.published && <a href={`/blogs/${b.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-paper-2"><ExternalLink className="w-4 h-4" /></a>}
              <button data-testid={`admin-blog-edit-${b.id}`} onClick={() => setEditing(b)} className="p-2 rounded-lg hover:bg-paper-2"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`admin-blog-delete-${b.id}`} onClick={() => remove(b)} className="p-2 rounded-lg hover:bg-red-50 text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-lenis-prevent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          {editing && <BlogForm initial={editing} onSaved={() => { setEditing(null); load(); onChange(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
